import { emptyWeek, formatHours, parseHours, WEEK, type Week, type Weekday } from "../../../shared/hours";

// Setări → Restaurant → Program (DEC-030): one row per day, open or closed, opening and closing time. Kept as the
// compact string of shared/hours.ts in the public profile (draft until "Publică datele restaurantului").

const DAY_NAME = ["Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă", "Duminică"];
const DEFAULT: [string, string] = ["12:00", "22:00"];

export function HoursEditor({
  value,
  onChange,
  error,
  toggleLabel = "Afișează programul în meniul public",
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  /** The switch that turns the weekly program on (off = no program / always shown). */
  toggleLabel?: string;
}) {
  // A malformed value (never saved by this editor) is shown as empty rather than lost silently on save.
  const week: Week = parseHours(value ?? "") ?? emptyWeek();
  const shown = (value ?? "").trim() !== "";
  const update = (d: Weekday, intervals: [string, string][]) =>
    onChange(formatHours({ ...week, [d]: intervals }));
  const sameAsMonday = () => {
    const mon = week[1].length ? week[1] : [DEFAULT];
    onChange(
      formatHours(Object.fromEntries(WEEK.map((d) => [d, mon.map((i) => [...i])])) as unknown as Week),
    );
  };
  return (
    <div className="field">
      <label className="check-row">
        <input
          type="checkbox"
          checked={shown}
          onChange={(e) =>
            onChange(
              e.target.checked
                ? formatHours(Object.fromEntries(WEEK.map((d) => [d, [[...DEFAULT]]])) as unknown as Week)
                : "",
            )
          }
        />
        <span>{toggleLabel}</span>
      </label>
      {shown && (
        <div className="stack" style={{ gap: 8, marginTop: 8 }}>
          {WEEK.map((d) => {
            const iv = week[d][0];
            const open = !!iv;
            return (
              <div
                key={d}
                className="row hours-row"
                style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}
              >
                <label className="check-row" style={{ minWidth: 130 }}>
                  <input
                    type="checkbox"
                    checked={open}
                    onChange={(e) => update(d, e.target.checked ? [[...DEFAULT]] : [])}
                  />
                  <span>{DAY_NAME[d - 1]}</span>
                </label>
                {open ? (
                  <>
                    <label
                      className="sr-only"
                      htmlFor={`h-${d}-o`}
                    >{`${DAY_NAME[d - 1]}: deschide la`}</label>
                    <input
                      id={`h-${d}-o`}
                      className="field-input"
                      type="time"
                      style={{ width: 140, height: 40 }}
                      value={iv[0]}
                      onChange={(e) =>
                        e.target.value && update(d, [[e.target.value, iv[1]], ...week[d].slice(1)])
                      }
                    />
                    <span aria-hidden="true">–</span>
                    <label className="sr-only" htmlFor={`h-${d}-c`}>{`${DAY_NAME[d - 1]}: închide la`}</label>
                    <input
                      id={`h-${d}-c`}
                      className="field-input"
                      type="time"
                      style={{ width: 140, height: 40 }}
                      value={iv[1] === "24:00" ? "00:00" : iv[1]}
                      onChange={(e) =>
                        e.target.value &&
                        update(d, [
                          [iv[0], e.target.value === "00:00" ? "24:00" : e.target.value],
                          ...week[d].slice(1),
                        ])
                      }
                    />
                  </>
                ) : (
                  <span className="muted">Închis</span>
                )}
              </div>
            );
          })}
          <div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={sameAsMonday}>
              Aplică programul de luni tuturor zilelor
            </button>
          </div>
          <span className="field-help">
            O oră de închidere mai mică decât cea de deschidere înseamnă după miezul nopții (de ex.
            18:00–02:00).
          </span>
        </div>
      )}
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
